<script>
import router from "@/router";
import { layoutComputed, layoutMethods } from "@/state/helpers";
import { marcaState, assetUrl } from "@/services/marca";
import logoHorizontalPadrao from "@/assets/images/logo-pcdob-horizontal.png";
import logoIconePadrao from "@/assets/images/logo-pcdob-icon.png";

import NavBar from "@/components/nav-bar";
import Menu from "@/components/menu";
import RightBar from "@/components/right-bar";
import Footer from "@/components/footer";
import MobileBottomNav from "@/components/mobile-bottom-nav.vue";

/**
 * Layout vertical — sidebar colapsavel + topbar + conteudo + footer.
 * Estrutura identica ao tema Velzon (app-menu navbar-menu).
 */
export default {
  components: { NavBar, Menu, RightBar, Footer, MobileBottomNav },
  data() {
    return {
      isMenuCondensed: false,
    };
  },
  computed: {
    ...layoutComputed,
    // Logo/nome vindos da marca (whitelabel); fallback pro asset do bundle.
    logoHorizontal() {
      return marcaState.tem_logo_horizontal ? assetUrl("logo_horizontal") : logoHorizontalPadrao;
    },
    logoIcone() {
      if (marcaState.tem_logo_icone) return assetUrl("logo_icone");
      if (marcaState.tem_logo_horizontal) return assetUrl("logo_horizontal");
      return logoIconePadrao;
    },
    marcaNome() {
      return marcaState.nome_curto || "PCdoB";
    },
  },
  created: () => {
    document.body.classList.remove("sidebar-enable");
    document.body.classList.remove("vertical-sidebar-enable");
  },
  methods: {
    ...layoutMethods,

    updateSidebarSize() {
      let sidebarSize = "lg";
      if (window.innerWidth < 1025) {
        sidebarSize = "sm";
      }
      this.changeSidebarSize({ sidebarSize });
    },

    toggleSidebarPinned() {
      if (window.innerWidth < 1025) return;

      const atual = this.sidebarSize || "sm-hover";
      const proximo = atual === "sm-hover-active" ? "sm-hover" : "sm-hover-active";

      localStorage.setItem("hoverd", proximo === "sm-hover-active" ? "true" : "false");
      this.changeSidebarSize({ sidebarSize: proximo });
    },

    toggleMenu() {
      document.body.classList.toggle("sidebar-enable");
      if (window.screen.width >= 992) {
        router.afterEach(() => {
          document.body.classList.remove("sidebar-enable");
          document.body.classList.remove("vertical-collpsed");
        });
        document.body.classList.toggle("vertical-collpsed");
      } else {
        router.afterEach(() => {
          document.body.classList.remove("sidebar-enable");
        });
        document.body.classList.remove("vertical-collpsed");
      }
      this.isMenuCondensed = !this.isMenuCondensed;
    },

    toggleRightSidebar() {
      document.body.classList.toggle("right-bar-enabled");
    },

    hideRightSidebar() {
      document.body.classList.remove("right-bar-enabled");
    },
  },
  mounted() {
    const pinned = localStorage.getItem("hoverd") === "true";
    if (window.screen.width < 1025) {
      this.changeSidebarSize({ sidebarSize: "sm" });
    } else {
      this.changeSidebarSize({ sidebarSize: pinned ? "sm-hover-active" : "sm-hover" });
    }

    try {
      document.getElementById("overlay").addEventListener("click", () => {
        document.body.classList.remove("vertical-sidebar-enable");
      });
    } catch (_e) {
      // overlay pode nao existir
    }

    window.addEventListener("resize", () => {
      document.body.classList.remove("vertical-sidebar-enable");
      try {
        document.querySelector(".hamburger-icon").classList.add("open");
      } catch (_e) {
        // hamburger pode nao existir
      }
      this.updateSidebarSize();
    });

    // Teclado do mobile: mantém o app-shell do tamanho da área REALMENTE
    // visível (visualViewport), pra o header da conversa NÃO subir quando o
    // teclado abre. Complementa o `interactive-widget=resizes-content` (Android)
    // e cobre o iOS Safari, que ignora esse meta.
    this._syncViewportH = () => {
      const vv = window.visualViewport;
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty("--vvh", `${Math.round(h)}px`);
    };
    this._syncViewportH();
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this._syncViewportH);
    }
    window.addEventListener("resize", this._syncViewportH);
  },
  unmounted() {
    window.removeEventListener("resize", this.updateSidebarSize);
    if (this._syncViewportH) {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", this._syncViewportH);
      }
      window.removeEventListener("resize", this._syncViewportH);
    }
  },
};
</script>

<template>
  <div id="layout-wrapper">
    <NavBar />

    <!-- ========== App Menu (Sidebar) ========== -->
    <div class="app-menu navbar-menu">
      <!-- LOGO -->
      <div class="navbar-brand-box">
        <router-link to="/" class="logo logo-dark">
          <span class="logo-sm">
            <img :src="logoIcone" :alt="marcaNome" class="pcdob-logo-sm" />
          </span>
          <span class="logo-lg">
            <img :src="logoHorizontal" :alt="marcaNome" class="pcdob-logo-lg" />
          </span>
        </router-link>
        <router-link to="/" class="logo logo-light">
          <span class="logo-sm">
            <img :src="logoIcone" :alt="marcaNome" class="pcdob-logo-sm" />
          </span>
          <span class="logo-lg">
            <img :src="logoHorizontal" :alt="marcaNome" class="pcdob-logo-lg" />
          </span>
        </router-link>
        <button
          type="button"
          class="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover"
          id="vertical-hover"
          @click="toggleSidebarPinned"
        >
          <i class="ri-record-circle-line"></i>
        </button>
      </div>

      <div id="scrollbar">
        <Menu />
      </div>
      <div class="sidebar-background"></div>
    </div>
    <!-- ========== Fim App Menu ========== -->

    <div class="vertical-overlay"></div>

    <div class="main-content">
      <div class="page-content">
        <BContainer fluid>
          <slot />
        </BContainer>
      </div>
      <Footer />
    </div>
    <RightBar />

    <!-- Navegação inferior estilo app nativo (só no mobile) -->
    <MobileBottomNav />
  </div>
</template>

<style>
/* Faixa que pinta a área da barra de navegação do sistema (safe-area inferior)
   com o vermelho do tema — some o preto/branco que o SO desenharia ali.
   Fica acima do fundo do composer/página (z 1036 > bottom nav 1035) e abaixo
   de modais/backdrops. Altura 0 em telas sem gesture bar, então é inofensiva. */
body::after {
  content: "";
  position: fixed;
  left: 0; right: 0; bottom: 0;
  height: env(safe-area-inset-bottom, 0px);
  background: var(--vz-primary, #971219); /* vermelho no light */
  z-index: 1036;
  pointer-events: none;
}
[data-bs-theme='dark'] body::after { background: var(--brand-primary-dark, #3f060a); } /* vermelho escuro no dark */

/* ===== Dashboard: espaçamento vertical uniforme entre as linhas de cards =====
   O gutter-y do Bootstrap só espaça DENTRO de cada <BRow>; entre rows separadas
   os margins se anulam (gap inconsistente). Zeramos o gutter-y e usamos row-gap
   (quando os cols quebram) + margin-bottom (entre linhas). Desktop = 1.5rem;
   o @media mobile abaixo aperta pra 1rem. */
body.dashboard-page .dash-grid { --vz-gutter-y: 0; --bs-gutter-y: 0; row-gap: 1.5rem; margin-bottom: 1.5rem; }
body.dashboard-page .kpi-row { margin-bottom: 1.5rem; }

/* ===== Modo app nativo no mobile ===== */
@media (max-width: 767.98px) {
  /* Sem sidebar de menus no mobile: a navegação é a bottom nav. */
  #layout-wrapper .app-menu.navbar-menu { display: none !important; }
  #layout-wrapper .vertical-overlay { display: none !important; }
  /* Sem botão de hambúrguer na topbar (não há sidebar pra abrir). */
  #page-topbar #topnav-hamburger-icon { display: none !important; }
  /* Logo sempre visível no canto esquerdo da topbar. */
  #page-topbar .navbar-brand-box.horizontal-logo { display: inline-block !important; }
  /* Topbar com a MESMA altura/espaçamento do header de conversa
     (~60px, padding lateral 0.75rem) — transição lista↔conversa alinhada. */
  #page-topbar .navbar-header { height: 60px; padding: 0 0.75rem; }
  /* Logo cabe na topbar de 60px sem vazar. O tema dá `.logo { line-height: 70px }`,
     que estoura 10px abaixo — reduzimos para 60px. NÃO mexer no display de .logo
     (o tema mostra só .logo-dark OU .logo-light). */
  #page-topbar .navbar-brand-box { padding: 0; height: auto; }
  #page-topbar .navbar-brand-box .logo { line-height: 60px; }
  #page-topbar .pcdob-logo-lg,
  #page-topbar .pcdob-logo-sm { max-height: 38px; height: auto; width: auto; }
  /* === Logo da marca na topbar mobile (definitivo) ===
     O tema esconde a logo horizontal e mostra o slot de ícone quadrado (34px,
     object-fit:contain) — uma logo horizontal encolhe pra ~0 ali e "some" (fica
     no DOM, imagem carregada, mas sem tamanho visível). Aqui forçamos a logo
     HORIZONTAL com dimensões explícitas e sem clipping, sobrepondo tudo. */
  #page-topbar .navbar-brand-box.horizontal-logo:not(.tem-titulo) {
    display: inline-flex !important;
    align-items: center !important;
    width: auto !important;
    max-width: none !important;
    min-width: 0 !important;
    overflow: visible !important;
    padding: 0 !important;
  }
  /* NÃO forçar display em .logo — o tema esconde .logo-dark OU .logo-light
     (data-topbar). Forçar mostraria as duas. Só tiramos clipping/line-height. */
  #page-topbar .navbar-brand-box.horizontal-logo .logo {
    line-height: normal !important;
    overflow: visible !important;
  }
  #page-topbar .navbar-brand-box.horizontal-logo .logo .logo-sm { display: none !important; }
  #page-topbar .navbar-brand-box.horizontal-logo .logo .logo-lg {
    display: inline-flex !important;
    align-items: center !important;
    width: auto !important;
    overflow: visible !important;
  }
  #page-topbar .navbar-brand-box.horizontal-logo .pcdob-logo-lg {
    display: block !important;
    height: 42px !important;
    width: auto !important;
    max-width: 60vw !important;
    max-height: 42px !important;
    object-fit: contain !important;
    vertical-align: middle !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  /* Fora do Dashboard: some a logo e mostra o nome da seção (fonte grande). */
  #page-topbar .navbar-brand-box.horizontal-logo.tem-titulo { display: none !important; }
  #page-topbar .topbar-mobile-title {
    display: flex; align-items: center;
    height: 60px;
    padding-left: 8px;
    font-size: 1.2rem; font-weight: 500; line-height: 1;
    color: var(--vz-yellow, #fff100);
  }
  [data-bs-theme='dark'] #page-topbar .topbar-mobile-title { color: var(--vz-yellow, #f2c94c); }

  /* Páginas com scroll normal: reserva espaço pra bottom nav não cobrir. */
  .main-content .page-content { padding-bottom: calc(58px + env(safe-area-inset-bottom, 0px)); }

  /* Conteúdo colado nas bordas no mobile: zera o padding lateral do container-fluid do Layout. */
  .main-content .page-content > .container-fluid { padding-left: 0; padding-right: 0; }

  /* Dashboard: encosta o conteúdo logo abaixo da topbar (60px) no mobile, sem o
     vão extra reservado pra topbar de 70px do desktop. */
  body.dashboard-page .main-content .page-content { padding-top: calc(60px + 0.75rem); }

  /* Espaçamento uniforme entre os cards de gráfico empilhados no mobile. O
     gutter-y do Bootstrap só espaça DENTRO de cada row — as fronteiras entre
     rows separadas ficam sem gap. Zeramos o gutter-y e usamos row-gap (dentro
     da row) + margin-bottom (entre rows) pra um ritmo de 1rem uniforme. */
  body.dashboard-page .dash-grid { row-gap: 1rem; margin-bottom: 1rem; }

  /* KPIs (grid 2-col): o gap do meio = gutter-x (1rem) ficava maior que as
     laterais (0.75rem do page-content). Iguala reduzindo o gutter-x. */
  body.dashboard-page .kpi-row { --vz-gutter-x: 0.75rem; margin-bottom: 1rem; }

  /* Telas "fullscreen" (grupos/atendimento/canais) encolhem pra caber a
     bottom nav no modo lista. !important pra vencer o height próprio delas. */
  body.grupos-sem-footer .main-content,
  body.atendimento-sem-footer .main-content,
  body.canais-sem-footer .main-content {
    height: calc(100dvh - 58px - env(safe-area-inset-bottom, 0px)) !important;
  }
  /* Elas já zeram o padding-bottom do page-content — não precisam do reserve. */
  body.grupos-sem-footer .main-content .page-content,
  body.atendimento-sem-footer .main-content .page-content,
  body.canais-sem-footer .main-content .page-content { padding-bottom: 0; }

  /* Conversa (grupo/canal/atendimento) em fullscreen: ocupa a altura REALMENTE
     visível (--vvh, atualizado via visualViewport). Assim, quando o teclado
     abre, o shell encolhe pro tamanho visível e o header fica fixo no topo em
     vez de subir; sem teclado, --vvh == a tela cheia. */
  body.grupos-chat.grupos-sem-footer .main-content,
  body.canais-chat.canais-sem-footer .main-content,
  body.atendimento-chat.atendimento-sem-footer .main-content { height: var(--vvh, 100dvh) !important; }
}
</style>
